import React, { useState, useEffect } from 'react';
import { deviceTypeAPI, requestTypeAPI, departmentAPI, processorRuleAPI } from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, CpuChipIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('deviceTypes');
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [processorRules, setProcessorRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [deviceTypesRes, requestTypesRes, departmentsRes, processorRulesRes] = await Promise.all([
        deviceTypeAPI.getAll(),
        requestTypeAPI.getAll(),
        departmentAPI.getAll(),
        processorRuleAPI.getAll()
      ]);
      setDeviceTypes(deviceTypesRes.data);
      setRequestTypes(requestTypesRes.data);
      setDepartments(departmentsRes.data);
      setProcessorRules(processorRulesRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    if (type === 'deviceType') {
      setFormData(item ? {
        name: item.name,
        description: item.description || '',
        defaultLifespan: item.defaultLifespan || '',
        isActive: item.isActive
      } : {
        name: '',
        description: '',
        defaultLifespan: '',
        isActive: true
      });
    } else if (type === 'department') {
      setFormData(item ? {
        name: item.name,
        description: item.description || '',
        priority: item.priority || 5,
        isActive: item.isActive
      } : {
        name: '',
        description: '',
        priority: 5,
        isActive: true
      });
    } else if (type === 'processorRule') {
      setFormData(item ? {
        brand: item.brand,
        series: item.series,
        generationOffset: item.generationOffset,
        laptopMaxBehind: item.laptopMaxBehind,
        desktopMaxBehind: item.desktopMaxBehind,
        currentGeneration: item.currentGeneration,
        description: item.description || '',
        isActive: item.isActive
      } : {
        brand: '',
        series: '',
        generationOffset: 0,
        laptopMaxBehind: 8,
        desktopMaxBehind: 10,
        currentGeneration: 1,
        description: '',
        isActive: true
      });
    } else {
      setFormData(item ? {
        name: item.name,
        description: item.description || '',
        category: item.category,
        isActive: item.isActive
      } : {
        name: '',
        description: '',
        category: 'request',
        isActive: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalType === 'deviceType') {
        if (editingItem) {
          await deviceTypeAPI.update(editingItem.id, formData);
          toast.success('Cihaz türü güncellendi');
        } else {
          await deviceTypeAPI.create(formData);
          toast.success('Cihaz türü oluşturuldu');
        }
      } else if (modalType === 'department') {
        if (editingItem) {
          await departmentAPI.update(editingItem.id, formData);
          toast.success('Departman güncellendi');
        } else {
          await departmentAPI.create(formData);
          toast.success('Departman oluşturuldu');
        }
      } else if (modalType === 'processorRule') {
        if (editingItem) {
          await processorRuleAPI.update(editingItem.id, formData);
          toast.success('İşlemci kuralı güncellendi');
        } else {
          await processorRuleAPI.create(formData);
          toast.success('İşlemci kuralı oluşturuldu');
        }
      } else {
        if (editingItem) {
          await requestTypeAPI.update(editingItem.id, formData);
          toast.success('Talep türü güncellendi');
        } else {
          await requestTypeAPI.create(formData);
          toast.success('Talep türü oluşturuldu');
        }
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    try {
      if (type === 'deviceType') {
        await deviceTypeAPI.delete(id);
        toast.success('Cihaz türü silindi');
      } else if (type === 'department') {
        await departmentAPI.delete(id);
        toast.success('Departman silindi');
      } else if (type === 'processorRule') {
        await processorRuleAPI.delete(id);
        toast.success('İşlemci kuralı silindi');
      } else {
        await requestTypeAPI.delete(id);
        toast.success('Talep türü silindi');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Silme işlemi başarısız');
    }
  };

  const getPriorityBadge = (priority) => {
    if (priority >= 8) return { bg: 'bg-red-100', text: 'text-red-800', label: 'Yüksek' };
    if (priority >= 5) return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Orta' };
    return { bg: 'bg-green-100', text: 'text-green-800', label: 'Düşük' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Tanımlamalar</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('deviceTypes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'deviceTypes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cihaz Türleri
          </button>
          <button
            onClick={() => setActiveTab('requestTypes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'requestTypes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Talep Türleri
          </button>
          <button
            onClick={() => setActiveTab('departments')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'departments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Departmanlar
          </button>
          <button
            onClick={() => setActiveTab('processorRules')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
              activeTab === 'processorRules'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CpuChipIcon className="h-4 w-4" />
            Islemci Kurallari
          </button>
        </nav>
      </div>

      {/* Device Types */}
      {activeTab === 'deviceTypes' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="font-semibold">Cihaz Türleri</h2>
            <button
              onClick={() => openModal('deviceType')}
              className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Ekle
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Açıklama</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Varsayılan Ömür</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {deviceTypes.map((type) => (
                <tr key={type.id}>
                  <td className="px-6 py-4 font-medium">{type.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{type.description || '-'}</td>
                  <td className="px-6 py-4 text-sm">{type.defaultLifespan ? `${type.defaultLifespan} ay` : '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${type.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {type.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button onClick={() => openModal('deviceType', type)} className="text-blue-600 hover:text-blue-800">
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button onClick={() => handleDelete('deviceType', type.id)} className="text-red-600 hover:text-red-800">
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Request Types */}
      {activeTab === 'requestTypes' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="font-semibold">Talep Türleri</h2>
            <button
              onClick={() => openModal('requestType')}
              className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Ekle
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Açıklama</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {requestTypes.map((type) => (
                <tr key={type.id}>
                  <td className="px-6 py-4 font-medium">{type.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{type.description || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${type.category === 'issue' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                      {type.category === 'issue' ? 'Arıza' : 'Talep'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${type.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {type.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button onClick={() => openModal('requestType', type)} className="text-blue-600 hover:text-blue-800">
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button onClick={() => handleDelete('requestType', type.id)} className="text-red-600 hover:text-red-800">
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Departments */}
      {activeTab === 'departments' && (
        <div>
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-semibold">Departmanlar</h2>
              <button
                onClick={() => openModal('department')}
                className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Ekle
              </button>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Departman</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Öncelik</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seviye</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Açıklama</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {departments.map((dept) => {
                  const badge = getPriorityBadge(dept.priority);
                  return (
                    <tr key={dept.id}>
                      <td className="px-6 py-4 font-medium">{dept.name}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${dept.priority * 10}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">{dept.priority}/10</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{dept.description || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${dept.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {dept.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button onClick={() => openModal('department', dept)} className="text-blue-600 hover:text-blue-800">
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button onClick={() => handleDelete('department', dept.id)} className="text-red-600 hover:text-red-800">
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {departments.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                Henüz departman eklenmemiş
              </div>
            )}
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-800 mb-2">Öncelik Sistemi</h3>
            <p className="text-sm text-blue-700">
              Departman önceliği (1-10), değişim algoritmasında kullanılır. Yüksek öncelikli departmanlar
              cihaz değişimi için öncelikli olarak değerlendirilir.
            </p>
            <ul className="text-sm text-blue-700 mt-2 list-disc list-inside">
              <li><strong>8-10:</strong> Kritik departmanlar (Yönetim, Finans)</li>
              <li><strong>5-7:</strong> Orta öncelikli departmanlar (Satış, Üretim)</li>
              <li><strong>1-4:</strong> Düşük öncelikli departmanlar (Destek birimleri)</li>
            </ul>
          </div>
        </div>
      )}

      {/* Processor Rules */}
      {activeTab === 'processorRules' && (
        <div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
              <h2 className="font-semibold dark:text-white flex items-center gap-2">
                <CpuChipIcon className="h-5 w-5 text-indigo-600" />
                Islemci Kurallari
              </h2>
              <div className="flex gap-2">
                {processorRules.length === 0 && (
                  <button
                    onClick={async () => {
                      try {
                        await processorRuleAPI.seedDefaults();
                        toast.success('Varsayilan kurallar eklendi');
                        fetchData();
                      } catch (error) {
                        toast.error('Varsayilan kurallar eklenemedi');
                      }
                    }}
                    className="flex items-center px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-1" />
                    Varsayilan Ekle
                  </button>
                )}
                <button
                  onClick={() => openModal('processorRule')}
                  className="flex items-center px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Ekle
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Marka / Seri</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Guncel Nesil</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Offset</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Laptop Max</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Desktop Max</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Min Kabul</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Durum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Islem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {processorRules.map((rule) => {
                    const effectiveGen = rule.currentGeneration + rule.generationOffset;
                    const minLaptop = effectiveGen - rule.laptopMaxBehind;
                    const minDesktop = effectiveGen - rule.desktopMaxBehind;
                    return (
                      <tr key={rule.id} className={!rule.isActive ? 'opacity-50' : ''}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">{rule.brand} {rule.series}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{rule.description}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                            {rule.currentGeneration}. nesil
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">
                          {rule.generationOffset > 0 ? `+${rule.generationOffset}` : rule.generationOffset}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{rule.laptopMaxBehind} nesil</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{rule.desktopMaxBehind} nesil</td>
                        <td className="px-4 py-3">
                          <div className="text-xs">
                            <span className="text-green-600 dark:text-green-400">Laptop: {minLaptop}+</span>
                            <br />
                            <span className="text-blue-600 dark:text-blue-400">Desktop: {minDesktop}+</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={async () => {
                              try {
                                await processorRuleAPI.update(rule.id, { isActive: !rule.isActive });
                                toast.success(rule.isActive ? 'Kural pasif yapildi' : 'Kural aktif yapildi');
                                fetchData();
                              } catch (error) {
                                toast.error('Guncelleme basarisiz');
                              }
                            }}
                            className={`px-2 py-1 text-xs rounded-full ${
                              rule.isActive
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {rule.isActive ? 'Aktif' : 'Pasif'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button onClick={() => openModal('processorRule', rule)} className="text-indigo-600 hover:text-indigo-800">
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button onClick={() => handleDelete('processorRule', rule.id)} className="text-red-600 hover:text-red-800">
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {processorRules.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  Henuz kural eklenmemis. "Varsayilan Ekle" butonuna tiklayin.
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <h3 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2">Nasil Calisir?</h3>
            <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1">
              <li><strong>Nesil Offset:</strong> Ultra serisi icin +14 gibi, eski nesle donusturmek icin eklenen deger</li>
              <li><strong>Guncel Nesil:</strong> Bu serinin su anki en yeni nesli</li>
              <li><strong>Laptop/Desktop Max:</strong> Bu kadar nesil geride kalan cihazlar degisim listesine alinir</li>
              <li><strong>Ornek:</strong> Intel Ultra 2. nesil = 16. nesil esdeger (2 + 14 offset). Laptop 8 nesil geride kalabilir, yani 8. nesil ve uzeri kabul edilir.</li>
            </ul>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingItem ? 'Düzenle' : 'Yeni Ekle'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {modalType === 'processorRule' ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Marka</label>
                        <input
                          type="text"
                          required
                          value={formData.brand}
                          onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          placeholder="Intel, AMD"
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Seri</label>
                        <input
                          type="text"
                          required
                          value={formData.series}
                          onChange={(e) => setFormData({ ...formData, series: e.target.value })}
                          placeholder="i, Ultra, Ryzen"
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Guncel Nesil</label>
                        <input
                          type="number"
                          required
                          value={formData.currentGeneration}
                          onChange={(e) => setFormData({ ...formData, currentGeneration: parseInt(e.target.value) || 1 })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nesil Offset</label>
                        <input
                          type="number"
                          value={formData.generationOffset}
                          onChange={(e) => setFormData({ ...formData, generationOffset: parseInt(e.target.value) || 0 })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Laptop Max Geri</label>
                        <input
                          type="number"
                          value={formData.laptopMaxBehind}
                          onChange={(e) => setFormData({ ...formData, laptopMaxBehind: parseInt(e.target.value) || 8 })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Desktop Max Geri</label>
                        <input
                          type="number"
                          value={formData.desktopMaxBehind}
                          onChange={(e) => setFormData({ ...formData, desktopMaxBehind: parseInt(e.target.value) || 10 })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          min="1"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Aciklama</label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Intel Core i serisi (i3, i5, i7, i9)"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    {editingItem && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Aktif</label>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ad</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        rows="2"
                      />
                    </div>
                    {modalType === 'deviceType' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Varsayılan Ömür (Ay)</label>
                        <input
                          type="number"
                          value={formData.defaultLifespan}
                          onChange={(e) => setFormData({ ...formData, defaultLifespan: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          min="1"
                        />
                      </div>
                    )}
                    {modalType === 'department' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Öncelik Seviyesi: {formData.priority}/10
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={formData.priority}
                          onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                          className="mt-1 block w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Düşük (1)</span>
                          <span>Yüksek (10)</span>
                        </div>
                      </div>
                    )}
                    {modalType === 'requestType' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Kategori</label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="request">Talep</option>
                          <option value="issue">Arıza</option>
                        </select>
                      </div>
                    )}
                    {editingItem && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Aktif</label>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
